import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { NoteService } from "../shared/note.service";
import { Observable, of, Subject, timer } from "rxjs";
import { catchError, debounceTime, exhaustMap, filter, map, mergeMap, switchMap, takeUntil, tap } from "rxjs/operators";
import { ConfirmationService } from "primeng/api";
import { Note } from "../shared/note.model";
import { ILocationService } from "angular";
import { DraftNotesRowData, LogTreeNode } from "./draft-notes-table.model";
import { HttpErrorResponse } from "@angular/common/http";
import { LogService } from "../../logs/log.service";
import { PagedEntity } from "app/shared/model/app.model";
import { NoteControlService } from "ng/src/app/notes/note-controller/note-control.service";

@Component({
	templateUrl: 'draft-notes.component.html',
	styleUrls: ['draft-notes.component.css'],
	selector: 'draft-notes-selector'
})
export class DraftNotesComponent implements OnInit, OnDestroy {
	@Input() multiLog = false;
	@Input('checkNotes') checkNotes?: Observable<any>;
	@Input() collapseLargeSection = true;
	@Input() filterTemplateId: number | null = null;
	
	@Output() noteEditStarted = new EventEmitter();
	
	constructor(
		private noteService: NoteService,
		private logService: LogService,
		private confirmService: ConfirmationService,
		private noteControlService: NoteControlService,
		@Inject('$location') private $location: ILocationService
	) { }
	
	loadingDraftNotes = false;
	noteDeletionSubject = new Subject<Note>();
	treeData: LogTreeNode[] = [];

	get deleteButtonTooltipPosition() {
		return window.innerWidth > 1243 ? 'top' : 'left';
	}
	
	private deletePending = false;
	private destroySubject = new Subject();
	
	readonly destroy$ = this.destroySubject.asObservable();
	
	ngOnInit(): void {
		this.checkNotes?.pipe(
			takeUntil(this.destroySubject),
			switchMap(() => this.fetchDraftNotes())
		)
		.subscribe(data => this.treeData = data);
		
		(window as any).X = timer(0, 10000).pipe(
			takeUntil(this.destroySubject),
			filter(() => !this.deletePending),
			exhaustMap(() => this.fetchDraftNotes())
		)
		.subscribe((data) => this.treeData = data);

		this.noteDeletionSubject.pipe(
			takeUntil(this.destroySubject),
			tap(() => this.deletePending = true),
			mergeMap(note => 
				this.noteService.deleteNote(note)
					.pipe(
						catchError((err) => of(err))
					)
			),
			debounceTime(5000), //Disable syncing for 5 seconds after deleting a note to prevent syncing from interrupting the user
			tap(() => this.deletePending = false)
		).subscribe((data: LogTreeNode[] | Error) => {
			if (Array.isArray(data))
				this.treeData = data;
		});
	}
	
	ngOnDestroy(): void {
		this.destroySubject.next();
	}
	
	displayDeleteConfirm(note: Note, event: Event, logIx: number, templateIx: number, noteIx: number) {
		this.confirmService.confirm({
			key: note.id.toString(),
			target: event.target!,
			message: 'Are you sure you want to delete this draft?',
			icon: 'pi pi-exclamation-triangle',
			closeOnEscape: true,
			acceptButtonStyleClass: 'p-button-danger',
			rejectButtonStyleClass: 'p-button-secondary',
			acceptLabel: 'Delete',
			rejectLabel: 'Cancel',
			accept: () => {
				this.treeData[logIx].children[templateIx].children.splice(noteIx, 1);
				this.treeData = [...DraftNotesRowData.updateIndexes(this.treeData)];
				this.noteDeletionSubject.next(note);
			}
		})
	}
	
	editNote(note: Note) {
		if (note.logId !== LogService.selectedLog?.id) {
			this.logService.selectLog(note.logId);
		}
		
		this.noteControlService.start(note);
		this.noteEditStarted.emit();
	}
	
	private createDraftNoteData(resp: Note.Projections.KeyNoteTemplateValues[] | HttpErrorResponse | Error): LogTreeNode[] {
		if (!Array.isArray(resp))
			return [];

		
		if (!this.multiLog && LogService.selectedLog) {
			const logId = LogService.selectedLog.id;
			resp = resp.filter(note => note.logId === logId)
		}

		// expand logs/templates in the new data that are currently expanded
		const data = DraftNotesRowData.createFrom(resp, !this.collapseLargeSection);

		if (this.treeData.length) {
			data.forEach((log, logIx) => {
				const oldLog = this.treeData.find(tLog => tLog.data.id === log.data.id);
				if (oldLog)
					log.expanded = oldLog.expanded;
				
				log.children?.forEach((template, templateIx) => {
					const oldTemplate = oldLog?.children?.find(tTemplate => tTemplate.data.id === template.data.id);
					if (oldTemplate)
						template.expanded = oldTemplate.expanded;
				});
			});
		}
		
		return data;
	}
	
	private fetchDraftNotes(): Observable<LogTreeNode[]> {
		this.loadingDraftNotes = true;
		return this.noteService.getDraftNotesForUser({ template: this.filterTemplateId ?? undefined })
			.pipe(
				map(notes => {
					if (this.filterTemplateId) {
						notes = PagedEntity.from(notes.filter(note => note.templateId === this.filterTemplateId), notes.page);
					}
					return this.createDraftNoteData(notes);
				}),
				tap(() => this.loadingDraftNotes = false)
			)
	}
}